// Page Blog Mobile - Design unifié avec la charte graphique
// Header simple "Blog" + Onglets filtres + Loupe recherche fullscreen

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, X, Clock, FileText, Heart, MessageCircle, Eye, Loader2,
  RefreshCw, User, Calendar, ChevronRight, Bookmark
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Catégories de blog
const CATEGORIES = [
  { value: 'tous', label: 'Tous' },
  { value: 'conseils', label: 'Conseils' },
  { value: 'tutoriels', label: 'Tutoriels' },
  { value: 'actualites', label: 'Actualités' },
  { value: 'temoignages', label: 'Témoignages' },
  { value: 'tendances', label: 'Tendances' },
];

// Composant Article Card
function ArticleCard({ article, onClick }) {
  const [saved, setSaved] = useState(false);
  
  // Formater la date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  
  // Estimer le temps de lecture
  const readTime = article.read_time || Math.ceil((article.content?.length || 500) / 1000) || 3;
  
  return (
    <motion.article
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => onClick(article)}
    >
      {/* Image de couverture */}
      {article.cover_image && (
        <div className="relative h-40 bg-gray-100">
          <img 
            src={article.cover_image} 
            alt={article.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          {article.category && (
            <span className="absolute top-3 left-3 px-2.5 py-1 bg-[#1E3A5F] text-white text-xs font-semibold rounded-full">
              {article.category}
            </span>
          )}
          <button 
            onClick={(e) => { e.stopPropagation(); setSaved(!saved); }}
            className="absolute top-3 right-3 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow-sm"
          >
            <Bookmark size={16} className={saved ? 'text-orange-500 fill-orange-500' : 'text-gray-600'} />
          </button>
        </div>
      )}
      
      {/* Contenu */}
      <div className="p-4">
        <h3 className="font-bold text-gray-900 text-base mb-2 line-clamp-2">{article.title}</h3>
        
        {article.excerpt && (
          <p className="text-sm text-gray-600 line-clamp-2 mb-3">{article.excerpt}</p>
        )}
        
        {/* Métadonnées */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Calendar size={12} />
              {formatDate(article.created_at || article.published_at)}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={12} />
              {readTime} min
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1">
              <Eye size={12} />
              {article.views || 0}
            </span>
            <span className="flex items-center gap-1">
              <Heart size={12} />
              {article.likes || 0}
            </span>
          </div>
        </div>
        
        {/* Auteur */}
        {article.author && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
            {article.author.avatar ? (
              <img src={article.author.avatar} alt="" className="w-6 h-6 rounded-full object-cover" />
            ) : (
              <div className="w-6 h-6 rounded-full bg-[#1E3A5F] flex items-center justify-center text-white text-xs font-bold">
                {(article.author.name || 'A')[0]}
              </div>
            )}
            <span className="text-xs text-gray-700 font-medium">{article.author.name || 'Rédaction'}</span>
          </div>
        )}
      </div>
    </motion.article>
  );
}

// Modal Article complet
function ArticleModal({ article, onClose }) {
  if (!article) return null;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }} 
      className="fixed inset-0 z-50 bg-white overflow-y-auto"
    >
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10">
        <button onClick={onClose} className="p-2 -ml-2 hover:bg-gray-100 rounded-full">
          <X size={24} className="text-gray-700" />
        </button>
        <div className="flex items-center gap-2">
          <button className="p-2 hover:bg-gray-100 rounded-full">
            <Bookmark size={20} className="text-gray-600" />
          </button>
        </div>
      </div>
      
      {/* Contenu */}
      <article className="px-4 py-6 max-w-2xl mx-auto">
        {article.category && (
          <span className="inline-block px-3 py-1 bg-orange-100 text-orange-600 text-xs font-semibold rounded-full mb-4">
            {article.category}
          </span>
        )}
        
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{article.title}</h1>
        
        {/* Auteur et date */}
        <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-200">
          {article.author?.avatar ? (
            <img src={article.author.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-[#1E3A5F] flex items-center justify-center text-white font-bold">
              {(article.author?.name || 'R')[0]}
            </div>
          )}
          <div>
            <p className="font-semibold text-gray-900">{article.author?.name || 'Rédaction Abracada'}</p>
            <p className="text-sm text-gray-500">{formatDate(article.created_at)}</p>
          </div>
        </div>
        
        {/* Image de couverture */}
        {article.cover_image && (
          <img 
            src={article.cover_image} 
            alt={article.title}
            className="w-full rounded-xl mb-6"
          />
        )}
        
        {/* Contenu de l'article */}
        <div 
          className="prose prose-sm max-w-none text-gray-700 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: article.content || article.excerpt || '' }}
        />
      </article>
    </motion.div>
  );
}

export default function MobileBlogPage({ isDesktopEmbed = false }) {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeCategory, setActiveCategory] = useState('tous');
  const [showFullscreenSearch, setShowFullscreenSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const searchInputRef = useRef(null);
  
  useEffect(() => {
    fetchArticles();
  }, []);
  
  useEffect(() => {
    try {
      const saved = localStorage.getItem('blog_recent_searches');
      if (saved) setRecentSearches(JSON.parse(saved).slice(0, 5));
    } catch {}
  }, []);
  
  useEffect(() => {
    if (showFullscreenSearch && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 250);
    }
  }, [showFullscreenSearch]);
  
  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/blog`);
      if (res.ok) {
        const data = await res.json();
        if (data && data.length > 0) {
          setArticles(data);
        } else {
          // Données fictives si l'API retourne un tableau vide
          setFallbackData();
        }
      } else {
        setFallbackData();
      }
    } catch (err) {
      console.error('Erreur:', err);
      setFallbackData();
    }
    setLoading(false);
  }, []);
  
  const setFallbackData = () => {
    setArticles([
      {
        id: '1',
        title: 'Comment bien préparer son projet de rénovation',
        excerpt: 'Découvrez les étapes essentielles pour réussir votre projet de rénovation, du budget à la sélection des artisans.',
        cover_image: 'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=800',
        category: 'Conseils',
        created_at: new Date().toISOString(),
        read_time: 5,
        views: 234,
        likes: 45,
        author: { name: 'Marie Dupont', avatar: null }
      },
      {
        id: '2',
        title: 'Les tendances déco 2025 pour votre intérieur',
        excerpt: 'Couleurs, matériaux, styles... Tour d\'horizon des tendances qui vont marquer l\'année.',
        cover_image: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=800',
        category: 'Tendances',
        created_at: new Date(Date.now() - 86400000).toISOString(),
        read_time: 4,
        views: 567,
        likes: 89,
        author: { name: 'Lucas Martin', avatar: null }
      },
      {
        id: '3',
        title: 'Témoignage : Ma rénovation de salle de bain',
        excerpt: 'Retour d\'expérience sur un projet de rénovation complète avec les artisans Abracada.',
        cover_image: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800',
        category: 'Témoignages',
        created_at: new Date(Date.now() - 172800000).toISOString(),
        read_time: 6,
        views: 189,
        likes: 34,
        author: { name: 'Sophie Bernard', avatar: null }
      },
      {
        id: '4',
        title: 'Tutoriel : Poser du carrelage comme un pro',
        excerpt: 'Guide pas à pas pour réussir la pose de votre carrelage, avec les conseils de nos experts.',
        cover_image: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800',
        category: 'Tutoriels',
        created_at: new Date(Date.now() - 259200000).toISOString(),
        read_time: 8,
        views: 892,
        likes: 156,
        author: { name: 'Jean Carreleur', avatar: null }
      },
      {
        id: '5',
        title: 'Actualité : Les nouvelles normes RT 2025',
        excerpt: 'Tout ce que vous devez savoir sur les nouvelles réglementations thermiques pour vos projets de construction.',
        cover_image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800',
        category: 'Actualités',
        created_at: new Date(Date.now() - 345600000).toISOString(),
        read_time: 7,
        views: 456,
        likes: 78,
        author: { name: 'Pierre Expert', avatar: null }
      },
    ]);
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchArticles();
    setRefreshing(false);
  };
  
  const saveRecentSearch = (query) => {
    if (!query || query.trim().length < 2) return;
    const trimmed = query.trim();
    const updated = [trimmed, ...recentSearches.filter(s => s.toLowerCase() !== trimmed.toLowerCase())].slice(0, 5);
    setRecentSearches(updated);
    try { localStorage.setItem('blog_recent_searches', JSON.stringify(updated)); } catch {}
  };
  
  const clearRecentSearches = () => {
    setRecentSearches([]);
    try { localStorage.removeItem('blog_recent_searches'); } catch {}
  };
  
  const handleSearchSubmit = (query) => {
    if (query && query.trim()) {
      saveRecentSearch(query);
      setSearchQuery(query.trim());
    }
    setShowFullscreenSearch(false);
  };
  
  // Filtrer les articles
  const filteredArticles = articles.filter(article => {
    // Filtre par catégorie
    if (activeCategory !== 'tous' && article.category?.toLowerCase() !== activeCategory) {
      return false;
    }
    // Filtre par recherche
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        article.title?.toLowerCase().includes(q) ||
        article.excerpt?.toLowerCase().includes(q) ||
        article.category?.toLowerCase().includes(q)
      );
    }
    return true;
  });
  
  return (
    <div className={isDesktopEmbed ? "bg-white" : "min-h-screen bg-gray-50"} style={{ paddingBottom: isDesktopEmbed ? '0' : '80px' }}>
      {/* Barre Bêta + Header */}
      {!isDesktopEmbed && (
        <>
          {/* Bandeau bêta supprimé - géré par TopBanner.jsx global */}
          
          <header className="bg-white border-b border-gray-200 sticky top-[30px] z-50">
            <div className="flex items-center justify-between h-14 px-3">
              <div className="flex items-center gap-0.5">
                <h1 className="text-lg font-bold text-[#1E3A5F]">Blog</h1>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={handleRefresh} disabled={refreshing} className="p-2 hover:bg-gray-100 rounded-full">
                  <RefreshCw size={20} className={`text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
                <button onClick={() => setShowFullscreenSearch(true)} className="p-2 hover:bg-gray-100 rounded-full" data-testid="blog-search-btn">
                  <Search size={20} className="text-gray-600" />
                </button>
              </div>
            </div>
            
            {/* Onglets Catégories - Style compact et propre */}
            <div 
              className="relative"
              style={{ background: '#FFFBF7' }}
            >
              <div className="flex items-center gap-2 px-3 py-2 overflow-x-auto scrollbar-hide">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.value}
                    onClick={() => setActiveCategory(cat.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                      activeCategory === cat.value 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-white text-gray-600 border border-gray-200'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
          </header>
        </>
      )}
      
      {/* Header pour mode Desktop embed */}
      {isDesktopEmbed && (
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-[#1E3A5F]">Blog</h2>
            <button onClick={() => setShowFullscreenSearch(true)} className="p-2 hover:bg-gray-100 rounded-full">
              <Search size={18} className="text-gray-600" />
            </button>
          </div>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {CATEGORIES.map(cat => (
              <button
                key={cat.value}
                onClick={() => setActiveCategory(cat.value)}
                className={`px-3 py-1.5 text-sm font-medium whitespace-nowrap rounded-full transition-all ${
                  activeCategory === cat.value 
                    ? 'bg-orange-500 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Contenu */}
      <div className="p-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="animate-spin text-orange-500" size={32} />
          </div>
        ) : filteredArticles.length === 0 ? (
          <div className="text-center py-12">
            <FileText size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Aucun article trouvé</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredArticles.map(article => (
              <ArticleCard 
                key={article.id} 
                article={article} 
                onClick={setSelectedArticle}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Recherche Fullscreen */}
      <AnimatePresence>
        {showFullscreenSearch && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-white z-[100]"
          >
            <div className="flex flex-col h-full">
              {/* Header recherche */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
                <button onClick={() => setShowFullscreenSearch(false)} className="p-1">
                  <X size={24} className="text-gray-600" />
                </button>
                <div className="flex-1 flex items-center bg-gray-100 rounded-xl px-3 py-2">
                  <Search size={18} className="text-gray-400 mr-2" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Rechercher un article..."
                    className="flex-1 bg-transparent outline-none text-sm"
                    defaultValue={searchQuery}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit(e.target.value)}
                  />
                </div>
              </div>
              
              {/* Recherches récentes */}
              <div className="flex-1 overflow-y-auto px-4 py-4">
                {recentSearches.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-gray-700">Recherches récentes</span>
                      <button onClick={clearRecentSearches} className="text-xs text-orange-500">Effacer</button>
                    </div>
                    <div className="space-y-2">
                      {recentSearches.map((search, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleSearchSubmit(search)}
                          className="flex items-center gap-3 w-full p-2 hover:bg-gray-50 rounded-lg"
                        >
                          <Clock size={16} className="text-gray-400" />
                          <span className="text-sm text-gray-700">{search}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Suggestions */}
                <div className="mt-6">
                  <span className="text-sm font-semibold text-gray-700 block mb-3">Suggestions</span>
                  <div className="flex flex-wrap gap-2">
                    {['Rénovation', 'Décoration', 'Économies', 'Écologie', 'Conseils'].map(tag => (
                      <button
                        key={tag}
                        onClick={() => handleSearchSubmit(tag)}
                        className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Modal Article */}
      <AnimatePresence>
        {selectedArticle && (
          <ArticleModal article={selectedArticle} onClose={() => setSelectedArticle(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}
